import { inject, Injectable } from '@angular/core';
import { ExpertUpdateType } from '@techspert-io/experts';
import {
  BehaviorSubject,
  defer,
  distinctUntilChanged,
  filter,
  ignoreElements,
  map,
  merge,
  Observable,
  share,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { ExpertStoreService } from '../../../shared/state/expert.service';
import { InterviewPeriod } from '../models/interview-period.models';
import { InterviewPeriodService } from './interview-period.service';

/** Expert updates that can change the interview period's times or calls */
const REFRESH_ON_UPDATES: readonly ExpertUpdateType[] = [
  ExpertUpdateType.ConfirmAvailability,
  ExpertUpdateType.ExpertSchedule,
  ExpertUpdateType.RequestDifferentTime,
];

export interface IInterviewPeriodResponse {
  body: InterviewPeriod;
  /** Sent back when saving, so a save can't overwrite a change made since */
  lastModified: string | null;
}

/** Undefined until first loaded, then null when it fails to load */
type Latest = IInterviewPeriodResponse | null | undefined;

const isSameResponse = (a: Latest, b: Latest) =>
  a?.lastModified === b?.lastModified &&
  JSON.stringify(a?.body) === JSON.stringify(b?.body);

@Injectable({
  providedIn: 'root',
})
export class InterviewPeriodStoreService {
  private readonly interviewPeriodService = inject(InterviewPeriodService);
  private readonly expertStoreService = inject(ExpertStoreService);

  /** Kept between pages, so the interview period shows at once on each */
  private readonly latest = new Map<string, BehaviorSubject<Latest>>();

  /**
   * Counts the saves, so a fetch started before a save, which may have read
   * the times before it, can't replace them once it returns
   */
  private readonly saveCounts = new Map<string, number>();

  /** Fetches while anything's subscribed, emitting nothing itself */
  private readonly loads = new Map<string, Observable<never>>();

  private readonly refresh$ = this.expertStoreService.completedUpdates$.pipe(
    filter(({ type }) => REFRESH_ON_UPDATES.includes(type))
  );

  /**
   * The opportunity's interview period with when it was last modified. Any
   * already loaded is emitted at once, then it's fetched again while anything
   * is subscribed, and after an expert update that can change it. Null when
   * it fails to load and none was loaded before.
   */
  getInterviewPeriodResponse(
    opportunityId: string
  ): Observable<IInterviewPeriodResponse | null> {
    return merge(
      this.getLoad(opportunityId),
      this.getLatest(opportunityId)
    ).pipe(
      filter(
        (response): response is IInterviewPeriodResponse | null =>
          response !== undefined
      )
    );
  }

  /** The opportunity's interview period, as `getInterviewPeriodResponse` */
  getInterviewPeriod(opportunityId: string) {
    return this.getInterviewPeriodResponse(opportunityId).pipe(
      map((response) => response?.body ?? null),
      distinctUntilChanged()
    );
  }

  /** Keeps the interview period as saved */
  setInterviewPeriod(
    opportunityId: string,
    response: IInterviewPeriodResponse
  ) {
    this.saveCounts.set(opportunityId, this.getSaveCount(opportunityId) + 1);
    this.keep(opportunityId, response);
  }

  /** Fetches the interview period now, keeping it when found */
  reloadInterviewPeriod(opportunityId: string) {
    return this.fetch(opportunityId);
  }

  private getSaveCount(opportunityId: string) {
    return this.saveCounts.get(opportunityId) ?? 0;
  }

  private getLatest(opportunityId: string) {
    if (!this.latest.has(opportunityId)) {
      this.latest.set(opportunityId, new BehaviorSubject<Latest>(undefined));
    }

    return this.latest.get(opportunityId);
  }

  private getLoad(opportunityId: string) {
    if (!this.loads.has(opportunityId)) {
      this.loads.set(
        opportunityId,
        this.refresh$.pipe(
          startWith(null),
          switchMap(() => this.fetch(opportunityId)),
          ignoreElements(),
          share()
        )
      );
    }

    return this.loads.get(opportunityId);
  }

  /**
   * A failed fetch keeps any interview period loaded before, as does a fetch
   * overtaken by a save
   */
  private fetch(opportunityId: string) {
    return defer(() => {
      const saveCount = this.getSaveCount(opportunityId);

      return this.interviewPeriodService.getByOpportunityId(opportunityId).pipe(
        tap((response) => {
          if (saveCount !== this.getSaveCount(opportunityId)) {
            return;
          }

          if (response || !this.getLatest(opportunityId).value) {
            this.keep(opportunityId, response);
          }
        })
      );
    });
  }

  /** Unchanged responses aren't emitted, so they don't replace the same */
  private keep(
    opportunityId: string,
    response: IInterviewPeriodResponse | null
  ) {
    const latest$ = this.getLatest(opportunityId);

    if (
      latest$.value === undefined ||
      !isSameResponse(latest$.value, response)
    ) {
      latest$.next(response);
    }
  }
}
